import { Mail } from "lucide-react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "../hooks/useAuth";
import {
  forgetPasswordSchema,
  type ForgetPasswordFormValues,
} from "../schema/auth.schema";
import SEO from "../components/SEO/SEO";

const ForgetPassword: React.FC = () => {
  const { requestPasswordReset } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [submitError, setSubmitError] = useState("");

  const form = useForm<ForgetPasswordFormValues>({
    resolver: zodResolver(forgetPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const handleSubmit = async (values: ForgetPasswordFormValues) => {
    setLoading(true);
    setMessage("");
    setSubmitError("");

    try {
      const { error } = await requestPasswordReset(values.email);
      if (error) {
        setSubmitError(error.message);
      } else {
        setMessage(
          "If this email is registered, a reset link has been sent to your email.",
        );
      }
    } catch {
      setSubmitError("Failed to send reset link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <SEO title="Forgot Password" description="Reset your My Perfume account password." noindex />
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg border border-neutral-200 overflow-hidden">
          <div className="bg-white p-8 text-center border-b border-neutral-200">
            <img
              src="/assets/Images/logo/logo.webp"
              alt="My Perfume"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h2 className="text-2xl font-bold text-neutral-800 mb-2">
              Reset Your Password
            </h2>
            <p className="text-neutral-600 text-sm">
              Enter your email and we&apos;ll send you a reset link.
            </p>
          </div>

          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 p-8">
            <div>
              <label
                htmlFor="reset-email"
                className="block text-sm font-medium text-neutral-700 mb-2"
              >
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                <input
                  id="reset-email"
                  type="email"
                  {...form.register("email")}
                  className="w-full pl-12 pr-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400"
                  placeholder="Enter your email"
                  disabled={loading}
                />
              </div>
              {form.formState.errors.email && (
                <div className="mt-2 text-sm text-red-600">
                  {form.formState.errors.email.message}
                </div>
              )}
            </div>

            {message && (
              <div className="text-green-700 text-sm bg-green-50 p-3 rounded-lg border border-green-200">
                {message}
              </div>
            )}
            {submitError && (
              <div className="text-red-700 text-sm bg-red-50 p-3 rounded-lg border border-red-200">
                {submitError}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary-light focus:ring-4 focus:ring-primary/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </button>
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default ForgetPassword;
